import * as Yup from 'yup'
import { getInitialState } from '../../../utils/formikUpdate'
import { fetchProvincias } from '../../../services/cantonService'

/**
 * Valida que un string sea un GeoJSON válido
 */
const isValidGeoJSON = value => {
  if (!value) return false
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value
    const validTypes = ['FeatureCollection', 'Feature', 'Polygon', 'MultiPolygon', 'Point', 'LineString', 'MultiPoint', 'MultiLineString', 'GeometryCollection']
    return parsed && validTypes.includes(parsed.type)
  } catch {
    return false
  }
}

export const datasetFormik = async () => {
  // Obtener provincias para el select
  let provinciasOptions = {}
  try {
    const provincias = await fetchProvincias()
    provincias.forEach(p => {
      provinciasOptions[p.id] = p.nombre
    })
  } catch (err) {
    console.error('Error loading provincias:', err)
  }

  // === PASO 1: INFORMACIÓN BÁSICA ===
  const STEP1_FIELDS = [
    {
      x: 12,
      name: 'nombre',
      initvalue: '',
      label: 'Nombre del Cantón *',
    },
    {
      x: 6,
      name: 'slug',
      initvalue: '',
      label: 'Slug (URL)',
      helperText: 'Opcional, se genera automáticamente',
    },
    {
      x: 6,
      name: 'id_provincia',
      initvalue: '',
      label: 'Provincia *',
      options: provinciasOptions,
    },
    {
      x: 6,
      name: 'habilitado',
      initvalue: 1,
      label: 'Estado',
      options: { 0: 'Desactivado', 1: 'Activado' },
    },
  ]

  // === PASO 2: BOUNDARY GEOJSON ===
  const STEP2_FIELDS = [
    {
      x: 12,
      name: 'boundary_geojson',
      initvalue: '',
      label: 'Boundary GeoJSON',
      isGeoJSONInput: true,
    },
  ]

  // === PASO 3: CONFIGURACIÓN AVANZADA ===
  const STEP3_FIELDS = [
    {
      x: 6,
      name: 'center_lng',
      initvalue: '',
      label: 'Longitud (Centro)',
      helperText: 'Opcional - se calcula automáticamente',
      type: 'number',
    },
    {
      x: 6,
      name: 'center_lat',
      initvalue: '',
      label: 'Latitud (Centro)',
      helperText: 'Opcional - se calcula automáticamente',
      type: 'number',
    },
    {
      x: 4,
      name: 'zoom',
      initvalue: '', // Ya no forzamos 14, dejamos que sea opcional/calculado
      label: 'Zoom',
      helperText: 'Valor entre 1-22',
      type: 'number',
    },
    {
      x: 4,
      name: 'bounds_sw_lng',
      initvalue: '',
      label: 'SW Longitud',
      helperText: 'Límite suroeste',
      type: 'number',
    },
    {
      x: 4,
      name: 'bounds_sw_lat',
      initvalue: '',
      label: 'SW Latitud',
      helperText: 'Límite suroeste',
      type: 'number',
    },
    {
      x: 6,
      name: 'bounds_ne_lng',
      initvalue: '',
      label: 'NE Longitud',
      helperText: 'Límite noreste',
      type: 'number',
    },
    {
      x: 6,
      name: 'bounds_ne_lat',
      initvalue: '',
      label: 'NE Latitud',
      helperText: 'Límite noreste',
      type: 'number',
    },
  ]

  // Todos los campos combinados
  const ITEMS_CANTON_FORMIK = [...STEP1_FIELDS, ...STEP2_FIELDS, ...STEP3_FIELDS]

  const INITIAL_FORMIK_STATE = getInitialState(ITEMS_CANTON_FORMIK)

  // Asegurar que el zoom por defecto sea 12 si está vacío, pero mejor lo manejamos como opcional puro
  // Si queremos pre-llenarlo, lo haríamos en el initvalue, pero el requerimiento es que sea auto-calculable.

  // Validaciones por paso
  const STEP1_VALIDATION = Yup.object().shape({
    nombre: Yup.string()
      .min(3, 'Mínimo 3 caracteres')
      .max(100, 'Máximo 100 caracteres')
      .required('Obligatorio'),
    slug: Yup.string()
      .min(2, 'Mínimo 2 caracteres')
      .max(50, 'Máximo 50 caracteres')
      .matches(/^[a-z0-9-]+$/, 'Solo letras minúsculas, números y guiones')
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value)),
    id_provincia: Yup.number()
      .required('Obligatorio')
      .typeError('Seleccione una provincia'),
  })

  // Paso 2: Boundary es opcional en teoría en la BD, pero recomendado.
  // En tenant lo hicimos required. En Cantón, el modelo permite null.
  // Pero para que la magia funcione, mejor pedirlo, o dejarlo opcional y validar warning.
  // El usuario pidió "lo mismo que en tenant", en tenant `boundary_geojson` es required.
  // Sin embargo, en Model.js de Canton es `allowNull: true`.
  // Para seguir "lo mismo que tenant", lo pondré requerido? No, el tenant tenía campos required.
  // Dejaré GeoJSON opcional pero si se pone, debe ser válido.

  const STEP2_VALIDATION = Yup.object().shape({
    boundary_geojson: Yup.string()
      .test('is-valid-geojson', 'Debe ser un GeoJSON válido', isValidGeoJSON)
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value)),
  })

  const STEP3_VALIDATION = Yup.object().shape({
    center_lng: Yup.number()
      .min(-180, 'Longitud mínima -180')
      .max(180, 'Longitud máxima 180')
      .nullable()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .typeError('Debe ser un número'),
    center_lat: Yup.number()
      .min(-90, 'Latitud mínima -90')
      .max(90, 'Latitud máxima 90')
      .nullable()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .typeError('Debe ser un número'),
    zoom: Yup.number()
      .min(1, 'Zoom mínimo 1')
      .max(22, 'Zoom máximo 22')
      .nullable()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .typeError('Debe ser un número'),
    bounds_sw_lng: Yup.number()
      .min(-180, 'Longitud mínima -180')
      .max(180, 'Longitud máxima 180')
      .nullable()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .typeError('Debe ser un número'),
    bounds_sw_lat: Yup.number()
      .min(-90, 'Latitud mínima -90')
      .max(90, 'Latitud máxima 90')
      .nullable()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .typeError('Debe ser un número'),
    bounds_ne_lng: Yup.number()
      .min(-180, 'Longitud mínima -180')
      .max(180, 'Longitud máxima 180')
      .nullable()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .typeError('Debe ser un número'),
    bounds_ne_lat: Yup.number()
      .min(-90, 'Latitud mínima -90')
      .max(90, 'Latitud máxima 90')
      .nullable()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .typeError('Debe ser un número'),
  })

  const FORM_VALIDATION = STEP1_VALIDATION
    .concat(STEP2_VALIDATION)
    .concat(STEP3_VALIDATION)

  return {
    INITIAL_FORMIK_STATE,
    ITEMS_CANTON_FORMIK,
    STEP1_FIELDS,
    STEP2_FIELDS,
    STEP3_FIELDS,
    STEP1_VALIDATION,
    STEP2_VALIDATION,
    STEP3_VALIDATION,
    FORM_VALIDATION,
  }
}
